/**
 * The ratio tile, and the two sentences that keep it honest.
 *
 * ── It is not called "productive ratio", because it is not one ──────────────
 *
 * Master §14.1 and spec 01 §12 both ask for a productive ratio. The classification
 * columns that would produce one — `productive_seconds`, `neutral_seconds`,
 * `unproductive_seconds`, `uncategorized_seconds` — exist in `wp_daily_aggregates`
 * and **no endpoint publishes any of them**: `readDailyRows` selects none and
 * `readTimeline` maps none. What is published is `online_seconds` and
 * `active_seconds`, whose quotient is a real number about a different question.
 *
 * So the tile is labelled "Tỷ lệ hoạt động" and carries the sentence saying which
 * ratio it is and which one it is not. Printing an active ratio under the word
 * "productive" is the same class of mistake as rendering a withheld metric as a
 * zero, one level up: the arithmetic is right, and the sentence it forms is false.
 *
 * ── What a half-partial window does and does not do to it ───────────────────
 *
 * The numerator and the denominator come from the same rows, so an hour nobody
 * observed is missing from both. What partial data biases is the **total**, not the
 * ratio. What it narrows is the **sample**, and the sample is printed beside the
 * percentage — never the percentage alone. Both halves of that are on screen,
 * because the half that reassures without the half that qualifies is a way of
 * dismissing the qualification.
 *
 * ── A denominator of zero prints no percentage ──────────────────────────────
 *
 * Not `0%`. `activeRatio` returns `no_observed_time` and there is no branch here
 * that turns it into a number: 0 out of 0 is not zero, and a ratio over no observed
 * time is not a ratio. Task 38 caps confidence rather than assigning zero for the
 * same reason.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { formatDurationSeconds } from '../../timer/elapsed';
import { activeRatio, type WindowFold } from '../../overview/fold';
import { CompletenessNote, hasGaps } from './CompletenessNote';
import { EmptyState } from '../../states/EmptyState';
import { Tile } from './Tile';

export function RatioTile({ fold }: { readonly fold: WindowFold }): ReactNode {
  const { t } = useI18n();
  const labels = { hour: t('common.hourShort'), minute: t('common.minuteShort') };
  const ratio = activeRatio(fold.totals);

  return (
    <Tile
      title={t('overview.ratio.title')}
      figure={
        ratio.kind === 'measured' ? (
          <span className="wp-figure">{`${String(Math.round(ratio.value * 100))}%`}</span>
        ) : (
          <EmptyState
            title={t('overview.ratio.none')}
            description={t('overview.ratio.none.next')}
            testId="k4-ratio-none"
          />
        )
      }
      detail={
        ratio.kind === 'measured' ? (
          <p className="wp-small" data-testid="k4-ratio-terms">
            {t('overview.ratio.terms', {
              active: formatDurationSeconds(ratio.activeSeconds, labels),
              online: formatDurationSeconds(ratio.onlineSeconds, labels),
            })}
          </p>
        ) : undefined
      }
      caveat={
        <>
          <p className="wp-small" data-testid="k4-ratio-not-productive">
            {t('overview.ratio.notProductive')}
          </p>
          {hasGaps(fold) ? (
            <p className="wp-small wp-muted" data-testid="k4-ratio-partial-note">
              {t('overview.ratio.partial')}
            </p>
          ) : null}
          <CompletenessNote fold={fold} testId="k4-ratio" />
        </>
      }
      // Same rule as the other two figures: colour marks a gap somebody has to close,
      // not the absence of a ratio (Luật Màu Có Nghĩa; finish review, round 2, fix 2).
      attention={hasGaps(fold)}
      testId="k4-ratio"
    />
  );
}
